import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.js'
import { getIconFromTaxa } from 'Util/iconUtil.js'
import { BookOpenIcon, ChartBarIcon, DocumentIcon, InformationCircleIcon, UsersIcon } from '@heroicons/react/24/outline'
import type { IResolvedParticipant } from '../../../Model/Project/IResolvedParticipant.js'
import ProjectParticipantWidget from './ProjectParticipantWidget.js'
import type { IProjectSample, ProjectOrder } from '../../../Model/Project/IProjectSample.js'
import { ProjectContextActions } from './ProjectContextActionsWidget.tsx'
import { selectSamplesLoading, selectTotalSamples, useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { RedlistModes } from '../../../Generated/RedlistMode.ts'
import { ProjectSampleWidget } from './ProjectSampleWidget.js'
import { ProjectNoteWidget } from './ProjectNoteWidget.js'
import { BlastStatisticsWidget } from './BlastStatisticsWidget.js'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import { useSelector } from 'react-redux'
import { canUseSpin } from '../../../Model/index.js'
import { describeSpinStatus } from '../../../Model/Spin/spinStatus.js'
import { SpinPendingBadge, useSpinScopeStatus } from '../../Component/SpinStatusLabel.js'

interface IProjectViewProps {
  project: IEvaluationProject
  items: IProjectSample[] | 'LOADING'
  participants: IResolvedParticipant[] | 'LOADING'
  orders: ProjectOrder[] | 'LOADING'
}

export function ProjectView({ project, items, participants, orders }: IProjectViewProps) {
  const currentUser = useGetAuthenticatedUser()
  const samplesLoading = useSelector(selectSamplesLoading)
  const sampleCount = useSelector(selectTotalSamples)
  const spinStatus = describeSpinStatus(useSpinScopeStatus(project.id, canUseSpin(currentUser)), 'PROJECT')

  const isReadOnly = !(
    currentUser &&
    (currentUser.id === project.ownerUser ||
      (participants != 'LOADING' && participants?.some((p) => p.participant.userId === currentUser.id && p.participant.mayEdit)))
  )

  const mapOwner = () => {
    if (!participants || participants == 'LOADING') {
      return 'loading...'
    }
    const resolved = participants.find((x) => x.participant.userId == project.ownerUser)?.user
    if (!resolved) {
      return 'unknown'
    }
    return `${resolved.email} (${resolved.org.name})`
  }

  const redlistLabel = project.redlistMode ? RedlistModes[project.redlistMode]?.label || project.redlistMode : 'Organization Default'

  return (
    <div className="bg-slate-100 rounded-lg p-4 drop-shadow-sm m-auto w-full">
      <div className="overflow-y-auto" style={{ minWidth: 900 }}>
        <div className={'flex items-stretch text-sm '}>
          <div className="flex-1 flex-col flex">
            <TooltipTrigger tooltip={'Your unique Project ID. Please refer to this in support requests. Click to copy to clipboard.'}>
              <span className={'text-neutral-400 align-baseline'} onClick={() => navigator.clipboard.writeText(project.id)}>
                Project#{project.id}
              </span>
            </TooltipTrigger>
          </div>
          <ProjectContextActions project={project} maxItemsToShow={2} readOnly={isReadOnly} />
        </div>
        <h1 className="text-2xl mb-4 pl-4 flex items-center">
          {project?.targetTaxonomy && getIconFromTaxa(project.targetTaxonomy, 'inline-block align-baseline mr-3')}
          <div className="relative">
            {project.name} {project.draftMode && <span className={'badge badge-warning'}>DRAFT</span>}
          </div>
          <SpinPendingBadge description={spinStatus} className="ml-3" />
          {/* orderToStateWidget(orderState, pipelineState)*/}
        </h1>
        <hr className={'py-2'} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Project Details</h2>
            <div className="space-y-2">
              <div>
                <span className="text-slate-500">Description:</span>
                <div className="relative">
                  <p>{project.description}</p>
                </div>
              </div>
              <div>
                <span className="text-slate-500">Owner:</span>
                <p>{mapOwner()}</p>
              </div>
              <div>
                <span className="text-slate-500">Redlist Mode:</span>
                <p>{redlistLabel}</p>
              </div>
              {project.version && (
                <div>
                  <span className="text-slate-500">Version:</span>
                  <p>{project.version}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Statistics</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <UsersIcon className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="text-lg font-medium">{participants === 'LOADING' ? 'loading' : participants?.length || 0}</p>
                  <p className="text-sm text-slate-500">Participants</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <DocumentIcon className="w-6 h-6 text-green-600" />
                <div>
                  <p className="text-lg font-medium">{sampleCount >= 0 ? sampleCount : 'loading'}</p>
                  <p className="text-sm text-slate-500">Samples</p>
                </div>
                {samplesLoading && <span className={'text-xs italic'}>Loading...</span>}
              </div>
              <div className="flex items-center gap-2">
                <BookOpenIcon className="w-6 h-6 text-sky-600" />
                <div>
                  <p className="text-lg font-medium">{orders === 'LOADING' ? 'loading' : orders?.length || 0}</p>
                  <p className="text-sm text-slate-500">Orders</p>
                </div>
              </div>
              <BlastStatisticsWidget projectId={project.id} />
            </div>
          </div>
        </div>

        <ProjectParticipantWidget participants={participants} project={project} currentUser={currentUser} readOnly={isReadOnly} />

        <ProjectNoteWidget project={project} participants={participants} currentUser={currentUser} readOnly={isReadOnly} />

        <ProjectSampleWidget samples={items} readOnly={isReadOnly} project={project} orders={orders === 'LOADING' ? [] : orders} />

        <div className="h-16" />
        <div className="dock">
          <button className="active" data-theme="lemonade">
            <InformationCircleIcon className="h-5 w-5" />
          </button>
          <button>
            <ChartBarIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
